import type { ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { QS_GLYPH } from "./wizard-icons";

/**
 * Quick Setup wizard configuration. The structural config (ids, types, phases,
 * icons) is static; all display strings come from the i18n catalog and are
 * resolved per-locale by `useWizardData()`. Ported from the Claude Design
 * prototype (quick-setup-data.jsx) and localized for ar/en.
 */

const K = keys.quickSetup;

export type ProductTypeId =
  | "fashion"
  | "beauty"
  | "perfumes"
  | "accessories"
  | "food"
  | "home"
  | "electronics"
  | "handmade"
  | "other";

export type StepPhase = "Products" | "Brand" | "Contact" | "Get paid" | "Delivery";

export type StepType =
  | "logo"
  | "producttypes"
  | "categories"
  | "whatsapp"
  | "payments"
  | "shipping";

export type SingleOption = {
  id: ProductTypeId;
  label: string;
  icon: ReactElement;
};

export type WizardStep = {
  id: string;
  label: string;
  q: string;
  /** One-line subhead shown under the heading in the new question-screen shell. */
  sub: string;
  type: StepType;
  phase: StepPhase;
  phaseName: string;
  minLabel?: string;
  options?: SingleOption[];
};

export type Phase = {
  id: StepPhase;
  name: string;
  desc: string;
  steps: Array<{ label: string; idx: number }>;
};

/** A category/product name carrying both locales so created records stay bilingual. */
export type CatPair = { ar: string; en: string };

// ── Bilingual content (not from the t() catalog: we need both locales at once
//    so created categories/products keep proper nameAr + nameEn) ──────────────
export const QS_CAT_SUGGEST: Record<string, CatPair[]> = {
  fashion: [
    { en: "Dresses", ar: "فساتين" },
    { en: "Tops", ar: "بلوزات" },
    { en: "Bottoms", ar: "بنطلونات" },
    { en: "Outerwear", ar: "ملابس خارجية" },
    { en: "Shoes", ar: "أحذية" },
    { en: "Accessories", ar: "إكسسوارات" },
  ],
  electronics: [
    { en: "Phones", ar: "هواتف" },
    { en: "Audio", ar: "صوتيات" },
    { en: "Computers", ar: "حواسيب" },
    { en: "Wearables", ar: "أجهزة قابلة للارتداء" },
    { en: "Accessories", ar: "إكسسوارات" },
  ],
  food: [
    { en: "Bakery", ar: "مخبوزات" },
    { en: "Drinks", ar: "مشروبات" },
    { en: "Snacks", ar: "وجبات خفيفة" },
    { en: "Meals", ar: "وجبات" },
    { en: "Desserts", ar: "حلويات" },
  ],
  beauty: [
    { en: "Skincare", ar: "العناية بالبشرة" },
    { en: "Makeup", ar: "مكياج" },
    { en: "Haircare", ar: "العناية بالشعر" },
    { en: "Fragrance", ar: "عطور" },
    { en: "Tools", ar: "أدوات" },
  ],
  perfumes: [
    { en: "Perfumes", ar: "عطور" },
    { en: "Oud", ar: "عود" },
    { en: "Incense", ar: "بخور" },
    { en: "Body Mists", ar: "معطرات الجسم" },
    { en: "Gift Sets", ar: "أطقم هدايا" },
  ],
  accessories: [
    { en: "Jewelry", ar: "مجوهرات" },
    { en: "Watches", ar: "ساعات" },
    { en: "Bags", ar: "شنط" },
    { en: "Sunglasses", ar: "نظارات شمسية" },
    { en: "Belts", ar: "أحزمة" },
  ],
  home: [
    { en: "Furniture", ar: "أثاث" },
    { en: "Decor", ar: "ديكور" },
    { en: "Kitchen", ar: "مطبخ" },
    { en: "Bedding", ar: "مفروشات" },
    { en: "Lighting", ar: "إضاءة" },
  ],
  handmade: [
    { en: "Jewelry", ar: "مجوهرات" },
    { en: "Candles", ar: "شموع" },
    { en: "Art", ar: "فن" },
    { en: "Ceramics", ar: "سيراميك" },
    { en: "Gifts", ar: "هدايا" },
  ],
  _default: [
    { en: "New Arrivals", ar: "وصل حديثًا" },
    { en: "Best Sellers", ar: "الأكثر مبيعًا" },
    { en: "Featured", ar: "مميّزة" },
    { en: "Sale", ar: "تخفيضات" },
  ],
};

/** Category suggestions for the v2 single-select sell-type answer. Freeform
 *  "other" (and no selection) intentionally returns no suggestions — there is
 *  no suggestion-generation endpoint, and merchandising fallbacks (e.g. "New
 *  Arrivals"/"Sale") are explicitly out per the redesign brief. */
export function suggestionsForType(type: ProductTypeId | null): CatPair[] {
  if (!type || type === "other") return [];
  return QS_CAT_SUGGEST[type] ?? [];
}

// ── Structural config (static) ───────────────────────────────────
// 5 top-level phases (handoff sidebar stepper): "Your products" groups the
// products + categories steps; the rest are 1:1 with a single step.
const PHASE_KEYS: Record<StepPhase, { name: string; desc: string }> = {
  Products: { name: K.phases.products, desc: K.phaseDesc.products },
  Brand: { name: K.phases.brand, desc: K.phaseDesc.brand },
  Contact: { name: K.phases.contact, desc: K.phaseDesc.contact },
  "Get paid": { name: K.phases.getPaid, desc: K.phaseDesc.getPaid },
  Delivery: { name: K.phases.delivery, desc: K.phaseDesc.delivery },
};

export const PRODUCT_TYPE_KEY: Record<ProductTypeId, string> = {
  fashion: K.productTypes.fashion,
  beauty: K.productTypes.beauty,
  perfumes: K.productTypes.perfumes,
  accessories: K.productTypes.accessories,
  food: K.productTypes.food,
  home: K.productTypes.home,
  electronics: K.productTypes.electronics,
  handmade: K.productTypes.handmade,
  other: K.productTypes.other,
};

/** Canonical English label per preset sell type, sent to the AI
 *  category-suggestions endpoint as the suggestion "phrase" regardless of the
 *  seller's active locale — keeps the global suggestion cache locale-
 *  independent (an Arabic-locale seller picking "Beauty & skincare" hits the
 *  same cache row as an English-locale seller picking the same preset).
 *  `other` is unused here: freeform sellers send their own typed text. */
export const PRODUCT_TYPE_EN: Record<ProductTypeId, string> = {
  fashion: "Fashion & clothing",
  beauty: "Beauty & skincare",
  perfumes: "Perfumes & oud",
  accessories: "Accessories & jewelry",
  food: "Food & drinks",
  home: "Home & decor",
  electronics: "Electronics",
  handmade: "Handmade & crafts",
  other: "",
};

type RawOption = { id: ProductTypeId; icon: ReactElement; labelKey: string };
type RawStep = {
  id: string;
  type: StepType;
  phase: StepPhase;
  labelKey: string;
  qKey: string;
  subKey: string;
  minLabelKey?: string;
  options?: RawOption[];
};

// Order: products (sell-type) → categories → logo → whatsapp → payments →
// shipping. Top-level phases: Products (products+categories), Brand (logo),
// Contact (whatsapp), Get paid (payments), Delivery (shipping).
//
// The visible sell-type cards (7, in order): fashion, beauty, perfumes,
// accessories, food, home, other. "electronics" and "handmade" are no longer
// shown as cards (those sellers pick "Something else"), but their
// ids/labels/suggestion maps are kept in the codebase (PRODUCT_TYPE_KEY,
// QS_CAT_SUGGEST) in case a future revision wants them back.
const RAW_STEPS: RawStep[] = [
  {
    id: "products",
    type: "producttypes",
    phase: "Products",
    labelKey: K.steps.type.label,
    qKey: K.steps.type.q,
    subKey: K.steps.type.sub,
    minLabelKey: K.steps.payment.minLabel,
    options: [
      { id: "fashion", icon: QS_GLYPH.shirt, labelKey: K.productTypes.fashion },
      { id: "beauty", icon: QS_GLYPH.sparkles, labelKey: K.productTypes.beauty },
      { id: "perfumes", icon: QS_GLYPH.droplet, labelKey: K.productTypes.perfumes },
      { id: "accessories", icon: QS_GLYPH.gem, labelKey: K.productTypes.accessories },
      { id: "food", icon: QS_GLYPH.cup, labelKey: K.productTypes.food },
      { id: "home", icon: QS_GLYPH.sofa, labelKey: K.productTypes.home },
      { id: "other", icon: QS_GLYPH.grid, labelKey: K.productTypes.other },
    ],
  },
  {
    id: "categories",
    type: "categories",
    phase: "Products",
    labelKey: K.steps.categories.label,
    qKey: K.steps.categories.q,
    subKey: K.steps.categories.sub,
  },
  { id: "logo", type: "logo", phase: "Brand", labelKey: K.steps.logo.label, qKey: K.steps.logo.q, subKey: K.steps.logo.sub },
  {
    id: "whatsapp",
    type: "whatsapp",
    phase: "Contact",
    labelKey: K.steps.whatsapp.label,
    qKey: K.steps.whatsapp.q,
    subKey: K.steps.whatsapp.sub,
  },
  {
    id: "payments",
    type: "payments",
    phase: "Get paid",
    labelKey: K.steps.payment.label,
    qKey: K.steps.payment.q,
    subKey: K.steps.payment.sub,
    minLabelKey: K.steps.payment.minLabel,
  },
  {
    id: "shipping",
    type: "shipping",
    phase: "Delivery",
    labelKey: K.steps.shipping.label,
    qKey: K.steps.shipping.q,
    subKey: K.steps.shipping.sub,
  },
];

/** Resolve the structural config into localized steps + phases for the active locale. */
export function useWizardData(): { steps: WizardStep[]; phases: Phase[] } {
  const t = useTranslations();

  const steps: WizardStep[] = RAW_STEPS.map((s) => ({
    id: s.id,
    type: s.type,
    phase: s.phase,
    phaseName: t(PHASE_KEYS[s.phase].name),
    label: t(s.labelKey),
    q: t(s.qKey),
    sub: t(s.subKey),
    minLabel: s.minLabelKey ? t(s.minLabelKey) : undefined,
    options: s.options?.map((o) => ({ id: o.id, icon: o.icon, label: t(o.labelKey) })),
  }));

  const phases: Phase[] = [];
  steps.forEach((s, i) => {
    let last = phases[phases.length - 1];
    if (!last || last.id !== s.phase) {
      last = { id: s.phase, name: t(PHASE_KEYS[s.phase].name), desc: t(PHASE_KEYS[s.phase].desc), steps: [] };
      phases.push(last);
    }
    last.steps.push({ label: s.label, idx: i });
  });

  return { steps, phases };
}
